import { CostTypeEnum, getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { Tabs, TabsStrategy } from '../../components/Tabs';
import {
  PATH_COST_ADMIN,
  PATH_COST_ADMIN_EDIT,
  PATH_COST_DEFAULT,
  PATH_COST_GENERAL,
  PATH_COST_GENERAL_EDIT,
  PATH_COST_VERSIONS,
} from '../../app/constants/paths.constant';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

import { Admin } from './Admin';
import { GeneralInfo } from './GeneralInfo';
import { Versions } from './Versions';
import type {
  CostPage_GeneralInfo_FixedCost_Fragment,
  CostPage_GeneralInfo_VariableCost_Fragment,
} from './GeneralInfo';

export const CostPage_Tabs_Cost_Fragment = graphql(`
  fragment CostPage_Tabs_Cost_Fragment on Cost {
    _id
    kind
    ...CostPage_Admin_Cost_Fragment
    ... on FixedCost {
      ...CostPage_GeneralInfo_FixedCost_Fragment
    }
    ... on VariableCost {
      ...CostPage_GeneralInfo_VariableCost_Fragment
    }
  }
`);

interface Props {
  readonly cost: FragmentType<typeof CostPage_Tabs_Cost_Fragment>;
}

export const CostTabs = ({ cost: pCost }: Readonly<Props>) => {
  const cost = getFragmentData(CostPage_Tabs_Cost_Fragment, pCost);
  const variableCost =
    cost.kind === CostTypeEnum.VariableCost
      ? (cost as FragmentType<
          typeof CostPage_GeneralInfo_VariableCost_Fragment
        >)
      : undefined;
  const fixedCost =
    cost.kind === CostTypeEnum.FixedCost
      ? (cost as FragmentType<typeof CostPage_GeneralInfo_FixedCost_Fragment>)
      : undefined;

  const { paths, getActiveTab } = useBuildPaths(
    {
      admin: PATH_COST_ADMIN,
      adminEdit: PATH_COST_ADMIN_EDIT,
      default: PATH_COST_DEFAULT,
      generalInfo: PATH_COST_GENERAL,
      generalInfoEdit: PATH_COST_GENERAL_EDIT,
      versions: PATH_COST_VERSIONS,
    },
    { id: cost._id },
    {
      admin: 'admin',
      adminEdit: 'admin',
      default: 'generalInfo',
      generalInfo: 'generalInfo',
      generalInfoEdit: 'generalInfo',
      versions: 'versions',
    },
  );

  return (
    <Tabs
      activeTab={getActiveTab()}
      id="cost-tabs"
      strategy={TabsStrategy.Controlled}
      tabs={[
        {
          key: 'generalInfo',
          label: 'Informations Générales',
          panel: (
            <GeneralInfo fixedCost={fixedCost} variableCost={variableCost} />
          ),
          to: paths.generalInfo,
          value: 'generalInfo',
        },
        {
          key: 'versions',
          label: 'Versions',
          panel: <Versions originalId={cost._id} />,
          to: paths.versions,
          value: 'versions',
        },
        {
          key: 'admin',
          label: 'Administration',
          panel: <Admin cost={cost} />,
          to: paths.admin,
          value: 'admin',
        },
      ]}
      title="Onglets de l'écran de Fiche d'un Coût"
    />
  );
};
